import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link, Navigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ConfirmEmailChange = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user, loading: authLoading, setUser } = useAuth();
    const token = params.get("token") || "";
    const [state, setState] = useState({ phase: "pending", message: "" });

    useEffect(() => {
        if (authLoading) return;
        if (!user) { setState({ phase: "must_sign_in", message: "" }); return; }
        if (!token) { setState({ phase: "error", message: "No confirmation token provided." }); return; }
        (async () => {
            try {
                const { data } = await api.post("/my/email-change/confirm", { token });
                // Refresh the user object in context with the new email
                setUser({ ...user, email: data.email });
                setState({ phase: "success", message: data.email });
                setTimeout(() => navigate("/account", { replace: true }), 2000);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                setState({ phase: "error", message: typeof detail === "string" ? detail : "Could not confirm." });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, authLoading, user?.user_id]);

    if (authLoading) return <div className="p-20 text-center text-[#4A5568]">Loading…</div>;
    if (state.phase === "must_sign_in") {
        const next = `/confirm-email-change?token=${encodeURIComponent(token)}`;
        return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
    }

    return (
        <div className="min-h-[60vh] flex items-center justify-center px-6 py-16" data-testid="confirm-email-page">
            <div className="text-center max-w-md">
                {state.phase === "pending" && (
                    <>
                        <Loader2 className="w-10 h-10 text-[#E53935] mx-auto mb-4 animate-spin" strokeWidth={1.5} />
                        <h1 className="font-heading text-2xl text-[#1C252E] mb-2">Confirming your new email…</h1>
                    </>
                )}
                {state.phase === "success" && (
                    <div data-testid="confirm-email-success">
                        <CheckCircle2 className="w-12 h-12 text-[#E53935] mx-auto mb-4" strokeWidth={1.5} />
                        <h1 className="font-heading text-3xl text-[#1C252E] mb-2">Email updated</h1>
                        <p className="text-sm text-[#4A5568]">Your account email is now <span className="font-medium text-[#1C252E]">{state.message}</span>. Taking you back to your account…</p>
                    </div>
                )}
                {state.phase === "error" && (
                    <div data-testid="confirm-email-error">
                        <AlertCircle className="w-12 h-12 text-[#E53935] mx-auto mb-4" strokeWidth={1.5} />
                        <h1 className="font-heading text-2xl text-[#1C252E] mb-2">Could not confirm</h1>
                        <p className="text-sm text-[#4A5568] mb-6">{state.message}</p>
                        <Link to="/account" className="inline-block bg-[#E53935] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 transition">
                            Back to my account
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConfirmEmailChange;
